import { list } from '@/services/api/resources/checklistNotifiable';
import { useChecklistNotifiablesStore } from '@/stores/checklist/checklistNotifiables';
import ChecklistNotifiableModal from '@/ui/components/checklist/assign/ChecklistNotifiableModal.vue';
import { Icon } from '@/ui/components/icon/Icon';
import { useTask } from '@/ui/concerns';
import { ChecklistNotifiableEditable, emptyChecklistNotifiable } from '@/ui/DTO';
import { actionHint } from '@/ui/helpers/scoreTriggerHelper';
import { defineComponent, h, ref, computed, onMounted } from 'vue';
import { button, col, cols, Tag } from '@/ui/components';
import { Tooltip } from '@/ui/components/Tooltip';
import { SelectOptions } from '@/ui/types';
import { upgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { isOnFreePlan } from '@/ui/auth/auth';
import { t } from '@/ui/i18n/i18n';
import { storeToRefs } from 'pinia';

interface ReceiveReportByMailProps {
  users: SelectOptions<number>;
  checklistId: null | string;
}

export const ReceiveReportByMail = defineComponent<ReceiveReportByMailProps>({
  props: ['users', 'checklistId'],
  setup(props) {
    const isEditing = computed(() => !!props.checklistId);
    const currentNotifiable = ref<ChecklistNotifiableEditable | null>(null);
    const isModalOpen = ref(false);
    const checklistNotifiablesStore = useChecklistNotifiablesStore();

    const { notifiables } = storeToRefs(checklistNotifiablesStore);

    const getNotifiables = useTask(
      () => list(props.checklistId!),
      response => {
        checklistNotifiablesStore.setNotifiables(response);
      },
    );

    const fetchNotifiables = () => {
      reset();

      if (isEditing.value) {
        getNotifiables.run();
      }
    };

    const reset = () => {
      checklistNotifiablesStore.$reset();
    };

    onMounted(fetchNotifiables);

    const toggleModal = (value: boolean, notifiable: ChecklistNotifiableEditable|null = null) => {
      isModalOpen.value = value;
      currentNotifiable.value = notifiable || null;
    };

    const openNew = () => {
      toggleModal(true, emptyChecklistNotifiable());
    };

    const openEdit = (notifiable: ChecklistNotifiableEditable) => {
      toggleModal(true, notifiable);
    };

    const save = (notifiable: ChecklistNotifiableEditable) => {
      if (notifiables.value.includes(currentNotifiable.value!)) {
        //this covers the case when we edit a notifiable from the list
        //it can be both a saved notifiable (with id) or a new one (without id)
        //"currentNotifiable" points to the object from the collection. "notifiable" is the clone.
        Object.assign(currentNotifiable.value!, notifiable);
      } else {
        checklistNotifiablesStore.addNotifiable(notifiable);
      }

      toggleModal(false);
    };

    const makeTitle = (notifiable: ChecklistNotifiableEditable) => {
      const usersList = notifiable.users.map(user => props.users.find(u => u.value === user)?.label).join(', ');
      const emailsList = notifiable.externalAssociates.join(', ');

      return [usersList, emailsList].filter(s => s).join(', ');
    };

    const remove = (notifiable: ChecklistNotifiableEditable) => {
      checklistNotifiablesStore.removeNotifiable(notifiable);
    };

    return function render() {
      return h('div', { class: 'checklist-builder__notifiables' }, [
        currentNotifiable.value ? h(ChecklistNotifiableModal, {
          props: {
            active: isModalOpen.value,
            notifiable: currentNotifiable.value,
            users: props.users,
          },
          on: {
            close: () => toggleModal(false),
            save,
          },
        }) : null,
        h('div', { class: 'checklist-builder__second-stage-title' }, [
          t('checklist.notifiables.title'),
          h(
            Tooltip,
            {
              props: {
                label: t('checklist.notifiables.trigger-hint'),
                position: 'is-top',
              },
            },
            [h(
              Tag,
              { class: 'tag--left-margin', props: { type: 'success' } },
              [
                t('new-features.new'),
                h(Icon, {
                  props: {
                    size: 'xs',
                    iconPack: 'fal',
                    icon: 'info-circle',
                  },
                }),
              ],
            )],
          ),
        ]),
        h('p', { class: 'text-sm text-70 mb-4' }, t('checklist.notifiables.description')),

        notifiables.value.map(notifiable => cols([
          col([
            h('div', makeTitle(notifiable)),
            notifiable.scoreTrigger && h('div', { class: 'text-sm text-70 mt-1' }, actionHint(notifiable.scoreTrigger)),
          ]),
          col([
            h(Icon, {
              class: 'mr-2 cursor-pointer has-text-grey',
              props: {
                icon: 'edit',
                size: '1.5x',
              },
              on: {
                click: () => openEdit(notifiable),
              },
            }),
            h(Icon, {
              class: 'cursor-pointer has-text-grey',
              props: {
                icon: 'trash-alt',
                size: '1.5x',
              },
              on: {
                click: () => remove(notifiable),
              },
            }),
          ], true),
        ])),

        h('div', { class: 'mt-4' }, [
          !isOnFreePlan.value && button(
            notifiables.value.length
              ? t('checklist.notifiables.add-people')
              : t('checklist.notifiables.yes-select-people'),
            openNew,
            undefined,
            { outlined: true },
          ),
          isOnFreePlan.value && [
            upgradeAd({
              message: t('checklist.notifiables.teaser'),
              isVertical: true,
              withButton: true,
              placeholder: t('checklist.notifiables.yes-select-people'),
            }),
          ],
        ]),
      ]);
    };
  },
});
